import { lightOrange, lightYellow, orange, yellow } from '@styles/theme';
import { createTheme, createThemeContract } from '@vanilla-extract/css';

const chipContract = { text: '', background: '' };

export const themeVars = createThemeContract({
  color: {
    primary: { main: '', hover: '' },
    error: '',
    success: '',
    chip: {
      blue: chipContract,
      green: chipContract,
      orange: chipContract,
      grey: chipContract,
      yellow: chipContract,
    },
    interactive: {
      ring: '',
      selectedBorder: '',
      selectedBackground: '',
      border: '',
      inputBorder: '',
      inputBackground: '',
    },
  },
  brandColor: {
    swiper: { pagination: { active: '', inactive: '' } },
    progressbar: { gradientStart: '', gradientEnd: '' },
    presentation: {
      background: { gradientStart: '', gradientEnd: '' },
      arrow: { gradientStart: '', gradientEnd: '' },
    },
  },
  font: { Roboto: '', Magenos: '' },
});

const font = {
  Roboto: "'Roboto', Times, sans-serif",
  Magenos: "'Magenos', sans-serif",
};

const white = '#ffffff';
// const black = '#000000';

export const neutral: Record<number, string> = {
  900: '#222222',
  800: '#3c3c3c',
  700: '#575757',
  600: '#717171',
  500: '#949494',
  400: '#b6b6b6',
  300: '#c6c6c6',
  200: '#d6d6d6',
  100: '#e5e5e5',
  50: '#f5f7fa',
};

export const pristineOceanic: Record<number, string> = {
  900: '#004c3c',
  800: '#006e56',
  700: '#008f70',
  600: '#00b18a',
  500: '#00d2a4',
  400: '#33dbb6',
  300: '#80e9d2',
  200: '#bff4e8',
  100: '#e6fbf6',
  50: '#f2fdfa',
};

export const blueRibbon: Record<number, string> = {
  900: '#002759',
  800: '#003a85',
  700: '#004eb3',
  600: '#005ed9',
  500: '#006fff',
  400: '#338cff',
  300: '#80b7ff',
  200: '#cce3ff',
  100: '#e6f1ff',
  50: '#f2f8ff',
};

export const parmaHam: Record<number, string> = {
  900: '#bd2d23',
  800: '#ca473c',
  700: '#d76255',
  600: '#e57c6f',
  500: '#f29788',
  400: '#ffb1a1',
  300: '#f2c7b3',
  200: '#f9dcc6',
  100: '#fff0d9',
  50: '#fff9f0',
};

export const tropicalForest: Record<number, string> = {
  900: '#002219',
  800: '#002c21',
  700: '#00362a',
  600: '#004133',
  500: '#004c3c',
  400: '#2f6a5c',
  300: '#739d92',
  200: '#a6c2ba',
  100: '#d2e0dc',
  50: '#edf3f1',
};

export const burntTangerine: Record<number, string> = {
  '900': '#4e0702',
  '800': '#740b04',
  '700': '#9d0f05',
  '600': '#be1206',
  '500': '#e01507',
  '400': '#ea6056',
  '300': '#f08f88',
  '200': '#f6b9b5',
  '100': '#fadcda',
  '50': '#fdf3f3',
};

export const coralGlow: Record<number, string> = {
  '900': '#593022',
  '800': '#854832',
  '700': '#b36144',
  '600': '#d97552',
  '500': '#ff8a61',
  '400': '#ffaf94',
  '300': '#ffc7b3',
  '200': '#ffdcd0',
  '100': '#ffede7',
  '50': '#fff9f7',
};

const commonColor = {
  error: burntTangerine[500],
  success: pristineOceanic[700],
  chip: {
    blue: { text: blueRibbon[500], background: blueRibbon[100] },
    green: { text: pristineOceanic[700], background: pristineOceanic[100] },
    grey: { text: tropicalForest[500], background: neutral[100] },
    orange: { text: orange, background: lightOrange },
    yellow: { text: yellow, background: lightYellow },
  },
};

export const efTheme = createTheme(themeVars, {
  color: {
    primary: { main: blueRibbon[500], hover: blueRibbon[700] },
    ...commonColor,
    interactive: {
      ring: pristineOceanic[700],
      selectedBorder: pristineOceanic[500],
      selectedBackground: pristineOceanic[100],
      border: neutral[100],
      inputBorder: neutral[300],
      inputBackground: white,
    },
  },
  brandColor: {
    swiper: {
      pagination: {
        active: pristineOceanic[700],
        inactive: pristineOceanic[500],
      },
    },
    progressbar: {
      gradientStart: pristineOceanic[500],
      gradientEnd: blueRibbon[500],
    },
    presentation: {
      background: {
        gradientStart: pristineOceanic[500],
        gradientEnd: pristineOceanic[200],
      },
      arrow: {
        gradientStart: blueRibbon[500],
        gradientEnd: pristineOceanic[500],
      },
    },
  },
  font,
});

export const ipacTheme = createTheme(themeVars, {
  color: {
    primary: { main: tropicalForest[500], hover: tropicalForest[700] },
    ...commonColor,
    interactive: {
      ring: tropicalForest[900],
      selectedBorder: tropicalForest[500],
      selectedBackground: tropicalForest[100],
      border: neutral[100],
      inputBorder: neutral[300],
      inputBackground: white,
    },
  },
  brandColor: {
    swiper: {
      pagination: { active: parmaHam[700], inactive: parmaHam[500] },
    },
    progressbar: { gradientStart: parmaHam[300], gradientEnd: parmaHam[500] },
    presentation: {
      background: { gradientStart: parmaHam[400], gradientEnd: parmaHam[100] },
      arrow: { gradientStart: parmaHam[900], gradientEnd: parmaHam[900] },
    },
  },
  font,
});
